/**
 * Builds display items for a bet slip, grouping custom bet outcomes
 * so they appear as one carousel item / card.
 *
 * @param {Object} bettingHistoryItem - bet slip from API (matches, events, outcomeMap)
 * @returns {Array<{
 *   type: 'regular'|'custom',
 *   match?: Object,
 *   event?: Object,
 *   baseMatch?: Object,
 *   customOutcome?: Object,
 *   outcomeDetails?: Array,
 *   outcomeStatus?: number
 * }>}
 */
export function getBetSlipDisplayItems(bettingHistoryItem) {
  const { matches = [], events = [], outcomeMap } = bettingHistoryItem;
  const primaryBets = outcomeMap?.primaryBets;
  const customBetOutcomes = outcomeMap?.customBetOutcomes;
  const rawOutcomes = outcomeMap?.rawOutcomes || {};

  // No custom bets → fall back to original matches
  if (!primaryBets || !customBetOutcomes || Object.keys(customBetOutcomes).length === 0) {
    return matches.map((match) => ({ type: 'regular', match }));
  }

  const displayItems = [];
  const primaryKeys = Object.keys(primaryBets).sort(
    (a, b) => (primaryBets[a].idx ?? 0) - (primaryBets[b].idx ?? 0)
  );

  for (const key of primaryKeys) {
    const primary = primaryBets[key];

    if (primary.outcomeType === 0) {
      // Regular bet → find matching match object via rawOutcomes
      const rawIdx = primary.idx;
      const raw = rawOutcomes[String(rawIdx)];
      const matchForRaw = raw
        ? matches.find(
            (m) =>
              m.eventId === raw.eventId &&
              m.marketId === raw.marketId &&
              String(m.outcomeId) === String(raw.outcomeId)
          )
        : null;
      if (matchForRaw) {
        displayItems.push({ type: 'regular', match: matchForRaw });
      }
    } else if (primary.outcomeType === 1 && customBetOutcomes[key]) {
      // Custom bet group → gather included outcomes
      const customOutcome = customBetOutcomes[key];
      const eventId = customOutcome.eventId;
      const includedOutcomes = customOutcome.includedOutcomes || [];
      const outcomeDetails = includedOutcomes
        .map((rawIdx) => rawOutcomes[String(rawIdx)])
        .filter(Boolean);

      const event =
        events.find((e) => e.eventId === eventId) || matches.find((m) => m.eventId === eventId);
      const baseMatch = matches.find((m) => m.eventId === eventId);

      const info = customOutcome.outcomeReportingInfo || {};
      let outcomeStatus = 2; // undecided
      if (info.isWon) outcomeStatus = 1;
      else if (info.isLost) outcomeStatus = 0;
      else if (info.isCancelled) outcomeStatus = 3;

      displayItems.push({
        type: 'custom',
        baseMatch,
        event,
        customOutcome,
        outcomeDetails,
        sportType: (baseMatch || event || {}).sportType ?? 0,
        outcomeStatus
      });
    }
  }

  return displayItems.length > 0
    ? displayItems
    : matches.map((match) => ({ type: 'regular', match }));
}
